/**
 * The WORKLIST banding — view B of the register alternatives, the owner's
 * chosen register structure (WEB TIER A · WT3).
 *
 * ===========================================================================
 * A REGISTER IS NOT A LIST OF DOCUMENTS, IT IS A QUEUE OF DECISIONS
 * ===========================================================================
 * The chosen frame's thesis, verbatim: group the rows by what they want from
 * you, and the ordering becomes the answer instead of a column you have to
 * read. Three bands, derived from the rows on screen and from nothing else:
 *
 *   late   — open, raised ≥ `OVERDUE_DAYS_IN_STAGE` days ago
 *   week   — open, everything else (the mockup's own else-branch: a PARTITION,
 *            so a renumbered threshold can never open a gap between the bands)
 *   closed — `isQuoteClosed(status)`, whatever its age. A quote won or lost
 *            three months ago is finished, not late.
 *
 * Band order is late / week / closed; an EMPTY band is ABSENT — filtered here,
 * never rendered as a bare head. Rows keep their incoming order (the server
 * answers raised-newest-first) inside each band: the banding regroups, it
 * never re-ranks.
 *
 * ===========================================================================
 * THE COUNT IS THE MEMBERS. THIS FILE MAKES THAT STRUCTURAL.
 * ===========================================================================
 * The band head's number is `band.rows.length` — reduced from the SAME array
 * whose rows render directly beneath the head, never a second query. The pure
 * function below returns the members and nothing numeric, so a page count
 * that disagrees with the rows is not something a caller can express.
 * `worklist.test.ts` pins the partition (no row lost, none twice).
 *
 * ===========================================================================
 * ONE CLOCK, STATED: THE BANDS READ RAISED-AGE, AND ONLY THAT
 * ===========================================================================
 * `age = daysSince(created_at)` — the document's age, the clock the chosen
 * frame's own `bands()` reads. The retired table's pill drew its ACCENT from a
 * second clock (`sent_at`, time in the customer's hands); view B's structure
 * deliberately does not — the band titles say "raised", and say so honestly.
 * An open row whose age is UNKNOWN (`daysSince` → null) cannot prove it is
 * late, and the alarm band must not be reachable by a guess, so it falls to
 * the quiet open band; its age cell prints the em dash rather than a number
 * (a derived decision, called out in the WT3 report — the mockup fixture never
 * produces the shape, but the wire allows it).
 *
 * The threshold is IMPORTED, not re-typed, and the late title interpolates it,
 * so an owner renumbering `OVERDUE_DAYS_IN_STAGE` re-titles the band on its
 * own — `domain/stages.ts` records that the constant is the one edit.
 *
 * ===========================================================================
 * THE BANDS ARE PAGE-SCOPED, AND ON A LONG REGISTER THAT UNDER-REPORTS
 * ===========================================================================
 * STATED HERE BECAUSE IT WAS PREVIOUSLY ONLY IMPLIED (prove-time finding
 * L1-1). `bandQuotes` partitions the rows it is handed, and the page hands it
 * ONE PAGE — `PER_PAGE = 50` of a server answer ordered `created_at DESC`
 * (`apps/api/.../quotes.service.ts`, "Newest first, then id"). Newest-first
 * means the OLDEST rows — the genuinely late ones — are on the LAST page. So
 * the alarm band describes the newest 50 quotes, not the backlog, and because
 * an empty band is filtered out it can vanish from page 1 altogether.
 *
 * MEASURED against this module (`scratchpad/mobile-v3/prove3-repro.log`):
 *
 *   60 open, 12 late  → page 1 shows "raised more than 7 days ago — 2"  (of 12)
 *   67 open, 12 late  → page 1 shows NO late band at all
 *   50 open, 12 late  → page 1 shows 12 of 12 ✓  (fits on one page)
 *
 * The view's thesis is that grouping IS the answer, and that thesis weakens
 * exactly when volume makes it matter. It is not silently tolerated: the
 * register's footer says so to the reader (`pageScopeNotice` below), and the
 * REAL fix is filed for an owner ruling as WEB-TIER-A-LEDGER item 23, because
 * every version of it changes `apps/api` — band-precedence ordering server
 * side, or a per-band count parameter — and this build opens no api file.
 * A web-only "fix" here would have to fetch every page to band honestly.
 */

import { OVERDUE_DAYS_IN_STAGE } from '../../domain/stages';
import { daysSince, isQuoteClosed } from '../../domain/quotes';
import type { QuoteStatus } from '../../domain/quotes';
import { NO_VALUE } from '../inquiries/format';

/**
 * The two fields banding actually reads — callers pass whole `QuoteDto`s.
 * `created_at` is nullable because the WIRE's is: a null raised-instant is the
 * unknown-age case the header describes (quiet band, em-dash age cell).
 */
export interface WorklistSource {
  status: QuoteStatus;
  created_at: string | null;
}

export type WorklistBandKey = 'late' | 'week' | 'closed';

export interface WorklistBand<T extends WorklistSource> {
  key: WorklistBandKey;
  /** The head's words. The late band's number comes from the constant. */
  title: string;
  /** The members — and therefore the count. `rows.length` IS the band head. */
  rows: T[];
}

/**
 * Partition ONE PAGE of quotes into the worklist's bands.
 *
 * Pure: rows in, bands out, `now` injectable so the threshold edges are
 * testable without clock-flap. Empty bands are already filtered.
 *
 * The caller decides what "one page" means, and today it is the newest 50 —
 * see the header's page-scope section for what that costs and who owns the
 * fix. This function cannot know about rows it was not given, which is why the
 * disclosure is the page's job (`pageScopeNotice`) rather than this one's.
 */
export function bandQuotes<T extends WorklistSource>(
  rows: readonly T[],
  now: Date = new Date(),
): WorklistBand<T>[] {
  const late: T[] = [];
  const week: T[] = [];
  const closed: T[] = [];

  for (const row of rows) {
    if (isQuoteClosed(row.status)) {
      closed.push(row);
    } else {
      const age = daysSince(row.created_at, now);
      if (age !== null && age >= OVERDUE_DAYS_IN_STAGE) late.push(row);
      else week.push(row);
    }
  }

  const bands: WorklistBand<T>[] = [
    {
      key: 'late',
      title: `Open, raised more than ${String(OVERDUE_DAYS_IN_STAGE)} days ago`,
      rows: late,
    },
    { key: 'week', title: 'Open, raised this week', rows: week },
    { key: 'closed', title: 'Closed', rows: closed },
  ];
  // An empty band is ABSENT, never rendered empty — the frame's own rule.
  return bands.filter((band) => band.rows.length > 0);
}

/**
 * The footer's page-scope sentence, or `null` when the whole answer is on
 * screen and there is nothing to disclose.
 *
 * WHY THIS EXISTS (prove-time, L1-1). The band heads count their own members,
 * so they can never disagree with the rows beneath them — but a reader has no
 * way to tell that "Open, raised more than 7 days ago — 2" means two ON THIS
 * PAGE rather than two in the country. When the answer spans pages, the
 * register says which of those it is. Silence would be the claim.
 *
 * The sentence is only printed when it is TRUE: a single-page answer is banded
 * over the complete result, and telling that reader about later pages would be
 * a warning about a problem they do not have.
 *
 * The threshold is interpolated from the same imported constant the late
 * band's title uses, so a renumbering moves both together.
 */
export function pageScopeNotice(shownRows: number, total: number): string | null {
  if (total <= shownRows) return null;
  return (
    `These groups cover this page only. The register answers newest first, so older ` +
    `quotes — including any raised more than ${String(OVERDUE_DAYS_IN_STAGE)} days ago — ` +
    `are on later pages.`
  );
}

/**
 * The age cell's wording — `1 day`, `12 days`, and the em dash for a row whose
 * age is unknown. NEVER `0` for unknown: claiming "today" when the truth is
 * absent misstates the one signal the cell carries (`daysSince`'s own ruling).
 */
export function ageLabel(age: number | null): string {
  if (age === null) return NO_VALUE;
  return age === 1 ? '1 day' : `${String(age)} days`;
}
